import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  standings,
  type Actor,
  type Heat,
  type Match,
  type Tournament,
} from "@spikeeasy/tournament";
import { bracketLayout } from "./bracket-layout";
import { MatchCard, StandingsTable } from "./TournamentViews";
import { Dialog, Field, teamName } from "./ui";

function slotName(t: Tournament, match: Match, side: 0 | 1): string {
  if (match.teams[side]) return teamName(t, match.teams[side]);
  const slot = match.slots[side];
  return "matchId" in slot
    ? `${slot.outcome === "winner" ? "Winner" : "Loser"} of ${slot.matchId}`
    : "Bye";
}
function matchScore(match: Match): [number, number] | null {
  if (match.status === "live") return match.points;
  if (!match.sets.length) return null;
  if (match.sets.length === 1) return match.sets[0]!;
  return [
    match.sets.filter(([a, b]) => a > b).length,
    match.sets.filter(([a, b]) => b > a).length,
  ];
}
function matchStatus(match: Match): string {
  if (match.resolution === "bye") return "Bye";
  if (match.resolution === "forfeit") return "Walkover";
  if (match.resolution === "retirement") return "Retired";
  if (match.status === "complete")
    return match.sets.length > 1 ? "Final · sets" : "Final";
  if (match.status === "live") return "Live";
  if (match.needsRelease && !match.released) return "Awaiting release";
  return match.status === "waiting" ? "To be decided" : "Upcoming";
}
function MatchRows({
  t,
  matches,
  select,
}: {
  t: Tournament;
  matches: Match[];
  select: (match: Match) => void;
}) {
  return (
    <ol className="fixture-list">
      {matches.map((match) => {
        const score = matchScore(match);
        return (
          <li key={match.id}>
            <button
              className={`fixture-row ${match.status}`}
              onClick={() => select(match)}
            >
              <span
                className={
                  match.winnerId === match.teams[0]
                    ? "fixture-team winner"
                    : "fixture-team"
                }
              >
                {slotName(t, match, 0)}
              </span>
              <span className="fixture-result">
                <strong>{score ? `${score[0]} : ${score[1]}` : "vs"}</strong>
                <small>
                  {match.stage === "bronze" ? "Third place · " : ""}
                  {matchStatus(match)}
                </small>
              </span>
              <span
                className={
                  match.winnerId === match.teams[1]
                    ? "fixture-team winner"
                    : "fixture-team"
                }
              >
                {slotName(t, match, 1)}
              </span>
              <span className="sr-only">Open match {match.id}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function EliminationBracket({
  t,
  matches,
  select,
}: {
  t: Tournament;
  matches: Match[];
  select: (match: Match) => void;
}) {
  const layout = useMemo(() => bracketLayout(matches), [matches]);
  const rounds = layout.headings.map((heading) => ({
    label: heading.label,
    matches: layout.nodes
      .filter(
        (node) =>
          node.column === heading.column &&
          node.row > heading.row &&
          !layout.headings.some(
            (next) =>
              next.column === heading.column &&
              next.row > heading.row &&
              next.row < node.row,
          ),
      )
      .map((node) => node.match),
  }));
  const [selectedRound, setRound] = useState(0);
  const round = Math.min(selectedRound, rounds.length - 1);
  const canvas = useRef<HTMLDivElement>(null);
  const [paths, setPaths] = useState<Array<{ d: string; loser: boolean }>>([]);
  useLayoutEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const measure = () => {
      const origin = element.getBoundingClientRect();
      const gap = Number.parseFloat(getComputedStyle(element).columnGap);
      const boxes = new Map(
        [...element.querySelectorAll<HTMLElement>("[data-bracket-match]")].map(
          (node) => [node.dataset.bracketMatch, node.getBoundingClientRect()],
        ),
      );
      setPaths(
        layout.edges.flatMap((edge) => {
          const from = boxes.get(edge.from);
          const to = boxes.get(edge.to);
          if (!from || !to) return [];
          const x1 = from.right - origin.left;
          const y1 = from.top + from.height / 2 - origin.top;
          const x2 = to.left - origin.left;
          const y2 = to.top + to.height / 2 - origin.top;
          const middle = x2 - gap / 2;
          return [
            {
              d: `M ${x1} ${y1} H ${middle} V ${y2} H ${x2}`,
              loser: edge.outcome === "loser",
            },
          ];
        }),
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    element
      .querySelectorAll("[data-bracket-match]")
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [layout]);
  return (
    <section className="stack">
      <div className="section-heading">
        <h3>Road to the final</h3>
        <small>Open a match for details</small>
      </div>
      <div className="bracket-mobile stack">
        <Field label="Bracket round">
          <select
            value={round}
            onChange={(event) => setRound(Number(event.target.value))}
          >
            {rounds.map((item, index) => (
              <option key={index} value={index}>
                {item.label}
              </option>
            ))}
          </select>
        </Field>
        <MatchRows
          t={t}
          matches={rounds[round]?.matches ?? []}
          select={select}
        />
        <div className="round-navigation">
          <button
            className="secondary"
            disabled={round === 0}
            onClick={() => setRound(round - 1)}
          >
            Previous round
          </button>
          <button
            className="secondary"
            disabled={round === rounds.length - 1}
            onClick={() => setRound(round + 1)}
          >
            Next round
          </button>
        </div>
      </div>
      <p className="muted bracket-desktop">
        Scroll across to follow each round.
        {t.config.mode === "double" &&
          " Solid lines follow winners; dashed lines follow losses. Each semifinal sends one team to the final."}
      </p>
      <div
        className="bracket-scroll bracket-desktop"
        role="region"
        aria-label="Elimination bracket, scroll horizontally to see all rounds"
        tabIndex={0}
      >
        <div
          className="bracket-canvas"
          ref={canvas}
          style={{
            gridTemplateColumns: `repeat(${layout.columns}, minmax(12rem, 1fr))`,
            minWidth: `calc(${layout.columns * 12}rem + ${layout.columns - 1} * var(--space-6))`,
          }}
        >
          <svg className="bracket-lines" aria-hidden="true">
            {paths.map((path, i) => (
              <path
                key={i}
                d={path.d}
                className={path.loser ? "loser-path" : ""}
              />
            ))}
          </svg>
          {layout.headings.map((heading, i) => (
            <h4
              className="bracket-heading"
              key={i}
              style={{ gridColumn: heading.column + 1, gridRow: heading.row }}
            >
              {heading.label}
            </h4>
          ))}
          {layout.nodes.map(({ match, column, row }) => {
            const score = matchScore(match);
            return (
              <button
                key={match.id}
                className={`bracket-match ${match.status}`}
                data-bracket-match={match.id}
                style={{ gridColumn: column + 1, gridRow: `${row} / span 2` }}
                onClick={() => select(match)}
              >
                <span className="bracket-meta">
                  <span>
                    {match.stage === "bronze" ? "Third place" : match.id}
                  </span>
                  <span>{matchStatus(match)}</span>
                </span>
                {([0, 1] as const).map((side) => (
                  <span
                    className={`bracket-team ${match.winnerId === match.teams[side] ? "winner" : ""}`}
                    key={side}
                  >
                    <span>{slotName(t, match, side)}</span>
                    <strong>{score?.[side] ?? ""}</strong>
                  </span>
                ))}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function DrawView({
  t,
  actor,
  onScore,
  onManage,
  onHeat,
}: {
  t: Tournament;
  actor: Actor | null;
  onScore: (match: Match) => void;
  onManage: (match: Match) => void;
  onHeat: (heat: Heat) => void;
}) {
  const [phase, setPhase] = useState<"qualifying" | "elimination" | null>(null);
  const [pool, setPool] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const elimination = useMemo(
    () => t.matches.filter((match) => !["pool", "swiss"].includes(match.stage)),
    [t.matches],
  );
  const hasQualifying =
    t.pools.length > 0 ||
    t.heats.length > 0 ||
    t.matches.some((match) => match.stage === "swiss");
  const currentPhase = elimination.length
    ? (phase ?? "elimination")
    : "qualifying";
  const selected = t.matches.find((match) => match.id === selectedId);
  const qualifyingLabel = t.pools.length
    ? "Pool play"
    : t.heats.length
      ? "Heats"
      : "Swiss rounds";
  return (
    <div className="stack">
      <div className="section-heading">
        <h2>The draw</h2>
        {hasQualifying && elimination.length > 0 && (
          <div
            className="draw-phases"
            role="group"
            aria-label="Tournament phase"
          >
            <button
              aria-pressed={currentPhase === "qualifying"}
              onClick={() => setPhase("qualifying")}
            >
              {qualifyingLabel}
            </button>
            <button
              aria-pressed={currentPhase === "elimination"}
              onClick={() => setPhase("elimination")}
            >
              Elimination
            </button>
          </div>
        )}
      </div>
      {currentPhase === "elimination" ? (
        <EliminationBracket
          t={t}
          matches={elimination}
          select={(match) => setSelectedId(match.id)}
        />
      ) : (
        <>
          {t.pools.length > 0 && (
            <>
              <div className="section-heading">
                <h3>Pool play</h3>
                <select
                  aria-label="Pool"
                  value={pool}
                  onChange={(e) => setPool(e.target.value)}
                >
                  <option value="all">All pools</option>
                  {t.pools.map((pool) => (
                    <option key={pool.id} value={pool.id}>
                      Pool {pool.id}
                    </option>
                  ))}
                </select>
              </div>
              <small>
                Sets and points show won : lost. Open a match for details.
              </small>
              {t.pools
                .filter((group) => pool === "all" || pool === group.id)
                .map((group) => (
                  <section className="pool-board" key={group.id}>
                    <div className="pool-table">
                      <div className="section-heading">
                        <h3>Pool {group.id}</h3>
                        <small>
                          {
                            t.courts.find((court) => court.id === group.courtId)
                              ?.name
                          }
                        </small>
                      </div>
                      <StandingsTable
                        t={t}
                        rows={standings(t, group.id)}
                        label={`Pool ${group.id} standings`}
                      />
                    </div>
                    <div className="pool-fixtures">
                      <h4>Matches</h4>
                      <MatchRows
                        t={t}
                        matches={t.matches.filter(
                          (match) => match.poolId === group.id,
                        )}
                        select={(match) => setSelectedId(match.id)}
                      />
                    </div>
                  </section>
                ))}
            </>
          )}
          {[
            ...new Set(
              t.matches
                .filter((match) => match.stage === "swiss")
                .map((match) => match.round),
            ),
          ].map((round) => (
            <section className="stack" key={round}>
              <h3>Swiss · Round {round}</h3>
              <MatchRows
                t={t}
                matches={t.matches.filter(
                  (match) => match.stage === "swiss" && match.round === round,
                )}
                select={(match) => setSelectedId(match.id)}
              />
            </section>
          ))}
          {t.heats.length > 0 && (
            <section className="stack">
              <h3>Heat groups</h3>
              <div className="match-grid">
                {t.heats.map((heat) => (
                  <article key={heat.id} className="match-card">
                    <strong>
                      Heat {heat.round} ·{" "}
                      {
                        t.courts.find((court) => court.id === heat.courtId)
                          ?.name
                      }
                    </strong>
                    <ul className="plain-list">
                      {heat.teamIds.map((id) => (
                        <li key={id}>
                          {teamName(t, id)}
                          {heat.totals && <strong>{heat.totals[id]}</strong>}
                        </li>
                      ))}
                    </ul>
                    <p className="muted">{heat.status}</p>
                    {actor?.role === "admin" && heat.status === "complete" && (
                      <button
                        className="text-button"
                        onClick={() => onHeat(heat)}
                      >
                        Correct totals
                      </button>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}
          {!t.playoffGenerated && (
            <p className="muted">
              {t.config.qualifiers
                ? `${t.config.qualifiers} teams will qualify. The bracket appears when the qualifying stage is complete and ties are resolved.`
                : "Final Swiss standings decide the tournament."}
            </p>
          )}
        </>
      )}
      {selected && (
        <Dialog
          title={`${selected.id} · Match details`}
          onClose={() => setSelectedId("")}
        >
          <MatchCard
            t={t}
            match={selected}
            actor={actor}
            onScore={(match) => {
              setSelectedId("");
              onScore(match);
            }}
            onManage={(match) => {
              setSelectedId("");
              onManage(match);
            }}
          />
        </Dialog>
      )}
    </div>
  );
}
